/**
 * Session Responses Component
 *
 * Simple detailed view of a single session's responses
 */

import React, { useState, useEffect } from 'react';
import { isDerivedTextFallback } from './photoTextFallback';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  MessageSquare,
  FileText,
  AlertTriangle,
  Mic,
  Keyboard,
  TrendingUp,
  TrendingDown,
  Sparkles,
} from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';
import { getSessionResponses } from '../../utils/surveyApi';
import { UI_TEXT } from '../../config/ui-text';
import PhotoAnswerImages from './PhotoAnswerImages';

const API_URL = import.meta.env.VITE_API_URL || '';

// Helper to build full URL for media
const getFullMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

interface ResponseItem {
  type:
    | 'single_choice'
    | 'multiple_choice'
    | 'yes_no'
    | 'rating_scale'
    | 'likert_scale'
    | 'short_text'
    | 'long_text'
    | 'dropdown'
    | 'nps'
    | 'numeric_input'
    | 'ranking'
    | 'matrix_rating'
    | 'max_diff'
    | 'conjoint_analysis'
    | 'original_response'
    | 'followup_question'
    | 'followup_response'
    | 'image_hotspot'
    | 'percentage_allocation'
    | 'photo_upload'
    | 'ai_dynamic_section_turns';
  value: any;
  timestamp: string;
}

/** One respondent turn inside an `ai_dynamic_section` interview (constants/ai_dynamic_section.py
 * `AI_DYNAMIC_SECTION_TURNS_TYPE` on the backend). Only `turn`, `text` and `answer` are read here —
 * the rest (`options`, `answered_at`, `targets_rubric_points`, …) are the section's own bookkeeping. */
interface AiDynamicSectionTurn {
  turn: number;
  text: string;
  answer: unknown;
  type?: string;
  question_id?: string;
}

/** The value shape stored under an `ai_dynamic_section_turns` response item. `started_at`,
 * `turns_asked` and `elapsed_seconds` are internal bookkeeping, deliberately not surfaced —
 * `stop_reason` is shown only when it signals the interview did NOT end normally. */
interface AiDynamicSectionValue {
  turns: AiDynamicSectionTurn[];
  stop_reason?: string | null;
}

interface SessionResponse {
  question_id?: string;
  question_text: string;
  question_type: string;
  response_data: ResponseItem[];
  created_at: string;
  quality_flags?: string[];
  response_time_seconds?: number;
  input_mode?: 'mic' | 'text' | null;
  media_urls?: string[];
  option_media?: { [optionIndex: number]: string[] };
  choices?: string[];
  state?: string;
  ai_inference?: {
    inferred_answer?: any;
    covered_by?: string[];
    citation?: string;
    confidence?: number;
    reason?: string;
  };
  // Adaptive question scoping: present when this question's initial ask was narrowed (action
  // 'narrow') or skipped via full-coverage carry-forward (action 'skip') based on earlier answers.
  adaptation?: {
    action?: string;
    authored_question?: string;
    adapted_question?: string;
    covered_points?: string[];
    inferred_quotes?: string[];
    remaining_points?: string[];
    covered_by?: string[];
  };
}

interface SessionResponsesData {
  session_id: number;
  completed_at: string;
  responses: SessionResponse[];
}

const SessionResponses: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<SessionResponsesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessionResponses();
  }, [sessionId]);

  const loadSessionResponses = async () => {
    if (!sessionId) return;

    try {
      setIsLoading(true);
      const result = await getSessionResponses(parseInt(sessionId));
      setSessionData(result);
    } catch (error) {
      console.error('Failed to load session responses:', error);
      setError(UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.ERRORS.FAILED_TO_LOAD_SESSION);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // The literal string routes_fastapi/survey.py's get_session_responses falls all the way back
  // to once none of current/snapshot/moderator question text exist for a response.
  const UNKNOWN_QUESTION_FALLBACK = 'Unknown Question';

  // ai_dynamic_section questions have no authored `text` (the section writes its own question
  // each turn), so the backend's resolution chain always lands on UNKNOWN_QUESTION_FALLBACK for
  // them. Give the researcher the same language the authoring UI already uses for this question
  // type (QuestionCard.tsx: "Adaptive section — writes its own questions") instead of a heading
  // that reads as missing data.
  const resolveQuestionHeading = (response: SessionResponse): string => {
    if (
      response.question_type === 'ai_dynamic_section' &&
      (!response.question_text || response.question_text === UNKNOWN_QUESTION_FALLBACK)
    ) {
      return UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.AI_DYNAMIC_SECTION_HEADING;
    }
    return response.question_text;
  };

  const getMediaType = (url: string | undefined) => {
    if (!url) return 'unknown';
    const lowerUrl = url.toLowerCase();

    // Check for image URLs
    if (
      lowerUrl.includes('imgur.com') ||
      lowerUrl.includes('unsplash.com') ||
      lowerUrl.includes('pexels.com') ||
      /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(lowerUrl)
    ) {
      return 'image';
    }

    // Check for video URLs
    if (
      lowerUrl.includes('youtube.com') ||
      lowerUrl.includes('youtu.be') ||
      lowerUrl.includes('vimeo.com') ||
      /\.(mp4|mov|avi|wmv|flv|webm)(\?|$)/i.test(lowerUrl)
    ) {
      return 'video';
    }

    return 'unknown';
  };

  // Helper to get option media for a chosen value
  const getOptionMediaForChoice = (
    chosenValue: string,
    choices: string[] | undefined,
    optionMedia: { [optionIndex: number]: string[] } | undefined,
  ): string[] => {
    if (!choices || !optionMedia) return [];
    const index = choices.indexOf(chosenValue);
    if (index === -1) return [];
    return optionMedia[index] || [];
  };

  // Helper to render option media thumbnails
  const renderOptionMediaThumbnails = (mediaUrls: string[]) => {
    if (!mediaUrls || mediaUrls.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {mediaUrls.map((url, idx) => {
          const mediaType = getMediaType(url);
          const fullUrl = getFullMediaUrl(url);

          return (
            <div
              key={idx}
              className="w-16 h-16 border border-gray-200 rounded-lg overflow-hidden bg-white"
            >
              {mediaType === 'image' ? (
                <img
                  src={fullUrl}
                  alt={`Option media ${idx + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : mediaType === 'video' ? (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
                  <span className="text-xs">📹</span>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
                  <span className="text-xs">📎</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderMediaContent = (mediaUrls: string[] | undefined) => {
    if (!mediaUrls || mediaUrls.length === 0) return null;

    return (
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mediaUrls.map((url, index) => {
            const mediaType = getMediaType(url);
            const fullUrl = getFullMediaUrl(url);

            return (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
              >
                {mediaType === 'image' ? (
                  <img
                    src={fullUrl}
                    alt={`Media ${index + 1}`}
                    className="w-full h-48 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                ) : mediaType === 'video' ? (
                  <video src={fullUrl} controls className="w-full h-48" preload="metadata" />
                ) : (
                  <div className="w-full h-48 flex items-center justify-center bg-gray-100 text-gray-500">
                    <div className="text-center">
                      <FileText className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">Media content</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderInputModeIndicator = (inputMode?: 'mic' | 'text' | null) => {
    if (!inputMode) return null;

    if (inputMode === 'mic') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 ml-2">
          <Mic className="w-3 h-3 mr-1" />
          Voice
        </span>
      );
    }

    if (inputMode === 'text') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2">
          <Keyboard className="w-3 h-3 mr-1" />
          Text
        </span>
      );
    }

    return null;
  };

  const renderQualityFlags = (qualityFlags: string[] = []) => {
    if (!qualityFlags || qualityFlags.length === 0) return null;

    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {qualityFlags.map((flag, index) => {
          const flagLabel =
            UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.QUALITY_FLAGS[
              flag.toUpperCase() as keyof typeof UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.QUALITY_FLAGS
            ] || flag;
          const flagDescription =
            UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.QUALITY_DESCRIPTIONS[
              flag.toUpperCase() as keyof typeof UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.QUALITY_DESCRIPTIONS
            ];

          return (
            <div
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200"
              title={flagDescription}
            >
              <AlertTriangle className="w-3 h-3 mr-1" />
              {flagLabel}
            </div>
          );
        })}
      </div>
    );
  };

  // Stop reasons that mean the interview did NOT run to a normal finish (constants/
  // ai_dynamic_section.py STOP_TIMEOUT / STOP_ABANDONED / STOP_ERROR). STOP_COVERED,
  // STOP_MAX_QUESTIONS and a null/absent reason (still short of its cap when the survey ended)
  // are all ordinary completions and stay silent.
  const AI_DYNAMIC_SECTION_ABNORMAL_STOPS = new Set(['timeout', 'abandoned', 'error']);

  // A turn's answer can be plain text, a matrix-style object (rating_scale/matrix_rating emits),
  // or a list (multiple_choice). Stringify whichever shape arrives rather than assuming text.
  const formatDynamicSectionAnswer = (answer: unknown): string => {
    if (answer === null || answer === undefined || answer === '') {
      return UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.NO_RESPONSE;
    }
    if (Array.isArray(answer)) {
      return answer.map((a) => String(a)).join(', ');
    }
    if (typeof answer === 'object') {
      return Object.entries(answer)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    }
    return String(answer);
  };

  // Renders an ai_dynamic_section interview as what it is: the question the AI asked and the
  // answer the respondent gave, per turn, in turn order. Internal bookkeeping (started_at,
  // turns_asked, elapsed_seconds) is deliberately left out — a researcher reading this needs the
  // conversation, not the section's runtime accounting.
  const renderAiDynamicSectionTurns = (value: AiDynamicSectionValue): React.ReactNode => {
    const L = UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS;
    const turns = [...(value.turns || [])].sort((a, b) => (a?.turn ?? 0) - (b?.turn ?? 0));
    const abnormalStop =
      !!value.stop_reason && AI_DYNAMIC_SECTION_ABNORMAL_STOPS.has(value.stop_reason);
    const stopLabel = abnormalStop
      ? L.AI_DYNAMIC_SECTION_STOP_REASONS[
          value.stop_reason as keyof typeof L.AI_DYNAMIC_SECTION_STOP_REASONS
        ]
      : null;

    return (
      <div className="space-y-3">
        {stopLabel && (
          <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
            <AlertTriangle className="w-3 h-3 mr-1" />
            {stopLabel}
          </div>
        )}
        {turns.length === 0 ? (
          <div className="text-sm text-gray-500">{L.NO_RESPONSE}</div>
        ) : (
          turns.map((turn, index) => (
            <div key={turn.question_id || index} className="space-y-1.5">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <div className="text-xs font-medium text-blue-700 mb-1">
                  {L.AI_DYNAMIC_SECTION_QUESTION}
                </div>
                <div className="text-sm text-blue-900 whitespace-pre-wrap">
                  {turn.text || L.NO_RESPONSE}
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="text-xs font-medium text-gray-600 mb-1">
                  {L.AI_DYNAMIC_SECTION_ANSWER}
                </div>
                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                  {formatDynamicSectionAnswer(turn.answer)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // A rapid-fire voice answer whose transcript is not (yet, or ever) back — response.state is
  // RESPONSE_STATES.TRANSCRIPTION_PENDING or TRANSCRIPTION_FAILED (constants/
  // interview_constants.py), set via SubmitResponseRequest.transcription_status. Distinguishable
  // from a genuinely empty/silent answer (state ANSWERED, value ''), which renders as before.
  const renderVoiceTranscriptStatus = (
    state: 'transcription_pending' | 'transcription_failed',
  ): React.ReactNode => {
    const L = UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS;
    const isFailed = state === 'transcription_failed';
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600 italic bg-gray-50 rounded-lg p-4">
        <span
          className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${isFailed ? 'bg-red-400' : 'bg-amber-400 animate-pulse'}`}
          aria-hidden="true"
        />
        <span>
          {isFailed ? L.VOICE_TRANSCRIPT_FAILED : L.VOICE_TRANSCRIPT_PENDING}
          <span className="block not-italic text-xs text-gray-400">
            {isFailed ? L.VOICE_TRANSCRIPT_FAILED_HINT : L.VOICE_TRANSCRIPT_PENDING_HINT}
          </span>
        </span>
      </div>
    );
  };

  // Universal response renderer that handles both text and JSX rendering
  const renderResponse = (
    responseData: any[],
    asJSX: boolean = false,
    state?: string,
  ): string | React.ReactNode => {
    if (!responseData || responseData.length === 0) {
      const label =
        state === 'conditionally_skipped'
          ? UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.CONDITIONALLY_SKIPPED
          : UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.NO_RESPONSE;
      return asJSX ? <div className="text-sm text-gray-500">{label}</div> : label;
    }

    // For single item responses in conversational context
    if (responseData.length === 1 && asJSX) {
      const item = responseData[0];
      const value = item.value;

      // Handle media responses
      if (typeof value === 'object' && value !== null && value.selected_urls) {
        return (
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-600 mb-2">
              Selected Media ({value.selected_urls.length} item
              {value.selected_urls.length !== 1 ? 's' : ''}):
            </div>
            <div className="grid grid-cols-2 gap-2">
              {value.selected_urls.map((url: string, urlIndex: number) => {
                const mediaType = getMediaType(url);
                return (
                  <div
                    key={urlIndex}
                    className="border border-gray-300 rounded overflow-hidden bg-white"
                  >
                    {mediaType === 'image' ? (
                      <img
                        src={url}
                        alt={`Selected media ${urlIndex + 1}`}
                        className="w-full h-16 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : mediaType === 'video' ? (
                      <div className="w-full h-16 flex items-center justify-center bg-gray-200 text-gray-600">
                        <div className="text-center text-xs">📹 Video</div>
                      </div>
                    ) : (
                      <div className="w-full h-16 flex items-center justify-center bg-gray-200 text-gray-600">
                        <div className="text-center text-xs">Media</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // Handle MaxDiff responses
      if (typeof value === 'object' && value !== null && ('most' in value || 'least' in value)) {
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-1 gap-2">
              {value.most && (
                <div className="bg-green-100 border border-green-300 rounded p-2">
                  <div className="text-xs font-medium text-green-700 mb-1">Most Appealing</div>
                  <div className="text-sm text-green-900">{value.most}</div>
                </div>
              )}
              {value.least && (
                <div className="bg-red-100 border border-red-300 rounded p-2">
                  <div className="text-xs font-medium text-red-700 mb-1">Least Appealing</div>
                  <div className="text-sm text-red-900">{value.least}</div>
                </div>
              )}
            </div>
            {value.set_index !== undefined && (
              <div className="text-xs text-gray-600">Set {value.set_index + 1}</div>
            )}
          </div>
        );
      }

      // Handle Conjoint Analysis responses
      if (
        typeof value === 'object' &&
        value !== null &&
        'choice' in value &&
        'scenario_index' in value
      ) {
        return (
          <div className="space-y-2">
            <div className="bg-blue-100 border border-blue-300 rounded p-2">
              <div className="text-xs font-medium text-blue-700 mb-1">Selected Choice</div>
              <div className="text-sm font-semibold text-blue-900">
                {value.choice?.replace('profile_', 'Option ')?.replace(/_/g, ' ') || value.choice}
              </div>
              {value.profile_details && typeof value.profile_details === 'object' && (
                <div className="mt-2 space-y-1">
                  {Object.entries(value.profile_details).map(([attr, val], idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-blue-700">{attr}:</span>
                      <span className="text-blue-900 font-medium">{String(val)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="text-xs text-gray-600">Scenario {(value.scenario_index || 0) + 1}</div>
          </div>
        );
      }

      // Handle ai_dynamic_section interview transcripts. MUST come before the matrix/grid
      // catch-all directly below: a section's stored value ({turns, started_at, stop_reason,
      // turns_asked, elapsed_seconds}) matches none of that branch's exclusions, so without this
      // check first it fell into "Matrix Ratings:" and stringified `turns` to "[object Object]".
      if (
        item.type === 'ai_dynamic_section_turns' &&
        typeof value === 'object' &&
        value !== null &&
        Array.isArray(value.turns)
      ) {
        return renderAiDynamicSectionTurns(value as AiDynamicSectionValue);
      }

      // Handle matrix/grid responses (exclude photo_upload which has 'images', 'all_extracted_products', or 'text_fallback')
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        !('choice' in value) &&
        !('most' in value) &&
        !('selected_urls' in value) &&
        !('clicks' in value) &&
        !('images' in value) &&
        !('all_extracted_products' in value) &&
        !('text_fallback' in value)
      ) {
        const entries = Object.entries(value);
        if (entries.length > 0 && entries.every(([key]) => typeof key === 'string')) {
          return (
            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-600 mb-2">Matrix Ratings:</div>
              {entries.map(([item, rating], index) => (
                <div
                  key={index}
                  className="flex justify-between items-center bg-white rounded border p-2"
                >
                  <span className="text-sm text-gray-900">{item}</span>
                  <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                    {String(rating)}
                  </div>
                </div>
              ))}
            </div>
          );
        }
      }

      // Handle NPS responses (0-10 scale with categories)
      if (item.type === 'nps' && typeof value === 'number' && value >= 0 && value <= 10) {
        const npsCategory = value <= 6 ? 'Detractor' : value <= 8 ? 'Passive' : 'Promoter';
        const categoryColor =
          value <= 6
            ? 'text-red-700 bg-red-100 border-red-200'
            : value <= 8
              ? 'text-yellow-700 bg-yellow-100 border-yellow-200'
              : 'text-green-700 bg-green-100 border-green-200';

        return (
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {value}
              </div>
              <div>
                <div className="text-sm font-medium text-gray-900">NPS Score: {value}/10</div>
                <div
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${categoryColor}`}
                >
                  {npsCategory}
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Handle rating scale responses (1-5, 1-7, etc.)
      if (item.type === 'rating_scale' && typeof value === 'number') {
        return (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
              {value}
            </div>
            <div className="text-sm font-medium text-gray-900">Rating: {value}</div>
          </div>
        );
      }

      // Handle Likert scale responses (text-based)
      if (
        typeof value === 'string' &&
        ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'].includes(value)
      ) {
        const likertColor =
          value === 'Strongly Disagree'
            ? 'text-red-700 bg-red-100 border-red-200'
            : value === 'Disagree'
              ? 'text-orange-700 bg-orange-100 border-orange-200'
              : value === 'Neutral'
                ? 'text-gray-700 bg-gray-100 border-gray-200'
                : value === 'Agree'
                  ? 'text-blue-700 bg-blue-100 border-blue-200'
                  : 'text-green-700 bg-green-100 border-green-200';

        return (
          <div className="flex items-center space-x-2">
            <div
              className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border ${likertColor}`}
            >
              {value}
            </div>
          </div>
        );
      }

      // Handle ranking responses
      if (Array.isArray(value)) {
        return (
          <div className="space-y-1">
            <div className="text-xs font-medium text-gray-600 mb-2">Ranking:</div>
            {value.map((choice: any, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <span className="text-sm text-gray-900">
                  {typeof choice === 'object' && choice?.choice === 'Other'
                    ? `Other: ${choice.other_text}`
                    : String(choice)}
                </span>
              </div>
            ))}
          </div>
        );
      }

      // Handle info_screen responses (just acknowledgment)
      if (item.type === 'info_screen') {
        return (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 text-teal-700 rounded-md">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Information viewed
            </span>
          </div>
        );
      }

      // Handle image_hotspot responses (also check original_response with clicks)
      if (
        (item.type === 'image_hotspot' || (item.type === 'original_response' && value?.clicks)) &&
        typeof value === 'object' &&
        value !== null &&
        value.clicks
      ) {
        const clicks = value.clicks as Array<{
          rank: number;
          x: number;
          y: number;
          product_id?: string;
          product_label?: string;
          timestamp_ms: number;
        }>;

        // Get rank colors for visual distinction
        const getRankColor = (rank: number): string => {
          const colors = [
            'bg-teal-500', // Rank 1
            'bg-blue-500', // Rank 2
            'bg-purple-500', // Rank 3
            'bg-orange-500', // Rank 4
            'bg-pink-500', // Rank 5
          ];
          return colors[(rank - 1) % colors.length];
        };

        return (
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-600 mb-2">Image Hotspot Selections:</div>
            <div className="space-y-1">
              {clicks.map((click, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-white rounded-lg p-2 border border-gray-100"
                >
                  <div
                    className={`w-6 h-6 ${getRankColor(click.rank)} rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
                  >
                    {click.rank}
                  </div>
                  <span className="text-sm text-gray-900 font-medium flex-1">
                    {click.product_label || 'Unknown Region'}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({(click.timestamp_ms / 1000).toFixed(1)}s)
                  </span>
                </div>
              ))}
            </div>
            {/* Total response time */}
            {clicks.length > 0 && (
              <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
                Total time: {(clicks[clicks.length - 1].timestamp_ms / 1000).toFixed(1)}s
              </div>
            )}
          </div>
        );
      }

      // Handle "Other" responses
      if (typeof value === 'object' && value?.choice === 'Other') {
        return (
          <div className="text-sm">
            <span className="font-medium">Other:</span> {value.other_text}
          </div>
        );
      }

      // Handle photo_upload responses - show per-image data with user input
      if (item.type === 'photo_upload' && typeof value === 'object' && value !== null) {
        return <PhotoAnswerImages value={value} />;
      }

      // A rapid-fire voice answer with a pending/failed transcript is intercepted earlier, in
      // renderConversationFlow, by response.state — it never reaches this generic renderer.

      // Handle simple values
      return <div className="text-sm whitespace-pre-wrap">{String(value || '')}</div>;
    }

    // For text format or multiple items, use the existing formatResponse logic
    const values = responseData
      .filter((item) =>
        [
          'single_choice',
          'multiple_choice',
          'yes_no',
          'rating_scale',
          'likert_scale',
          'short_text',
          'long_text',
          'dropdown',
          'nps',
          'numeric_input',
          'ranking',
          'matrix_ratings',
          'max_diff',
          'conjoint_analysis',
          'original_response',
          'followup_response',
          'image_hotspot',
          'percentage_allocation',
          'photo_upload',
          'ai_dynamic_section_turns',
        ].includes(item.type),
      )
      .map((item) => {
        const value = item.value;

        if (
          item.type === 'ai_dynamic_section_turns' &&
          typeof value === 'object' &&
          value !== null &&
          Array.isArray(value.turns)
        ) {
          return [...value.turns]
            .sort(
              (a: AiDynamicSectionTurn, b: AiDynamicSectionTurn) => (a?.turn ?? 0) - (b?.turn ?? 0),
            )
            .map(
              (t: AiDynamicSectionTurn) =>
                `Q: ${t.text || ''} | A: ${formatDynamicSectionAnswer(t.answer)}`,
            )
            .join(' / ');
        }

        // Handle all the same logic as formatResponse
        if (item.type === 'single_choice' && typeof value === 'object' && value?.selected_urls) {
          const selectedCount = value.selected_urls.length;
          return `Selected ${selectedCount} image${selectedCount !== 1 ? 's' : ''}`;
        }

        if (
          item.type === 'original_response' &&
          typeof value === 'object' &&
          value?.selected_urls
        ) {
          const selectedCount = value.selected_urls.length;
          return `Selected ${selectedCount} image${selectedCount !== 1 ? 's' : ''}`;
        }

        if (item.type === 'ranking' && Array.isArray(value)) {
          return value.map((choice, index) => `${index + 1}. ${choice}`).join(', ');
        }

        if (item.type === 'nps' && typeof value === 'number' && value >= 0 && value <= 10) {
          const npsCategory = value <= 6 ? 'Detractor' : value <= 8 ? 'Passive' : 'Promoter';
          return `${value}/10 (${npsCategory})`;
        }

        if (item.type === 'rating_scale' && typeof value === 'number') {
          return `Rating: ${value}`;
        }

        if (item.type === 'likert_scale' && typeof value === 'string') {
          return value;
        }

        if (item.type === 'matrix_ratings' && typeof value === 'object' && value !== null) {
          return Object.entries(value)
            .map(([item, rating]) => `${item}: ${rating}`)
            .join(', ');
        }

        // Handle percentage_allocation responses
        if (item.type === 'percentage_allocation' && typeof value === 'object' && value !== null) {
          return Object.entries(value)
            .map(([option, percentage]) => `${option}: ${percentage}%`)
            .join(', ');
        }

        // Handle original_response with percentage allocation pattern (object with all numeric values)
        if (
          item.type === 'original_response' &&
          typeof value === 'object' &&
          value !== null &&
          !('most' in value) &&
          !('least' in value) &&
          !('choice' in value) &&
          !('selected_urls' in value) &&
          !('clicks' in value) &&
          !Array.isArray(value)
        ) {
          const entries = Object.entries(value);
          // Check if all values are numbers (percentage allocation pattern)
          if (entries.length > 0 && entries.every(([, val]) => typeof val === 'number')) {
            return entries.map(([option, percentage]) => `${option}: ${percentage}%`).join(', ');
          }
        }

        if (item.type === 'max_diff' && typeof value === 'object' && value !== null) {
          const mostAppealing = value.most_appealing ? `Most: ${value.most_appealing}` : '';
          const leastAppealing = value.least_appealing ? `Least: ${value.least_appealing}` : '';
          const setInfo = value.set_index !== undefined ? ` (Set ${value.set_index + 1})` : '';
          return [mostAppealing, leastAppealing].filter(Boolean).join(', ') + setInfo;
        }

        if (item.type === 'conjoint_analysis' && typeof value === 'object' && value !== null) {
          const scenarioInfo =
            value.scenario_index !== undefined ? ` (Scenario ${value.scenario_index + 1})` : '';
          if (value.profile_details && typeof value.profile_details === 'object') {
            const profileDetails = Object.entries(value.profile_details)
              .map(([attr, val]) => `${attr}: ${val}`)
              .join(', ');
            return `Selected Option${scenarioInfo}: ${profileDetails}`;
          }
          const choice = value.choice
            ? value.choice.replace('profile_', 'Option ').replace(/_/g, ' ')
            : '';
          return `Selected: ${choice}${scenarioInfo}`;
        }

        if (
          item.type === 'original_response' &&
          typeof value === 'object' &&
          value !== null &&
          ('most' in value || 'least' in value)
        ) {
          const mostAppealing = value.most ? `Most: ${value.most}` : '';
          const leastAppealing = value.least ? `Least: ${value.least}` : '';
          const setInfo = value.set_index !== undefined ? ` (Set ${value.set_index + 1})` : '';
          return [mostAppealing, leastAppealing].filter(Boolean).join(', ') + setInfo;
        }

        if (
          item.type === 'original_response' &&
          typeof value === 'object' &&
          value !== null &&
          'choice' in value &&
          'scenario_index' in value
        ) {
          const scenarioInfo =
            value.scenario_index !== undefined ? ` (Scenario ${value.scenario_index + 1})` : '';
          if (value.profile_details && typeof value.profile_details === 'object') {
            const profileDetails = Object.entries(value.profile_details)
              .map(([attr, val]) => `${attr}: ${val}`)
              .join(', ');
            return `Selected Option${scenarioInfo}: ${profileDetails}`;
          }
          const choice = value.choice
            ? value.choice.replace('profile_', 'Option ').replace(/_/g, ' ')
            : '';
          return `Selected: ${choice}${scenarioInfo}`;
        }

        // Handle photo_upload type - per-image format with text input. Same three-way state as
        // the JSX renderer (PhotoAnswerImages): products found / rejected with a reason / not
        // analysed yet — never a fabricated "No products detected".
        if (item.type === 'photo_upload' && typeof value === 'object' && value !== null) {
          const images = value.images || [];

          const parts: string[] = images.map((img: any, idx: number) => {
            const imgParts: string[] = [`Image ${idx + 1}`];
            const products = img.extracted_products || [];
            const hasProducts = products.length > 0;
            const hasFailed = img.extraction_status === 'failed';
            const textInput = img.text_input || '';

            if (hasProducts) {
              const productStrings = products.map((p: any) =>
                p.brand ? `${p.name} (${p.brand})` : p.name,
              );
              imgParts.push(`Products: ${productStrings.join(', ')}`);
            } else if (hasFailed) {
              imgParts.push(
                `${UI_TEXT.HUMAN_SURVEY.PHOTO.AI_NOTE}: ${img.rejection_reason || UI_TEXT.HUMAN_SURVEY.PHOTO.REJECTED_NO_REASON}`,
              );
            } else {
              imgParts.push(UI_TEXT.HUMAN_SURVEY.PHOTO.NOT_ANALYSED);
            }
            // The respondent's typed text is never hidden, regardless of extraction state.
            if (textInput) {
              imgParts.push(`User input: ${textInput}`);
            }

            return `[${imgParts.join(' | ')}]`;
          });

          const base = parts.length > 0 ? parts.join(' ') : 'No response';
          return value.text_fallback &&
            !isDerivedTextFallback(value.text_fallback, value.images ?? [])
            ? `${base} | ${UI_TEXT.HUMAN_SURVEY.PHOTO.RESPONDENT_TEXT}: ${value.text_fallback}`
            : base;
        }

        // Handle info_screen responses in text format
        if (item.type === 'info_screen') {
          return 'Information viewed ✓';
        }

        // Handle image_hotspot responses in text format (also check original_response with clicks)
        if (
          (item.type === 'image_hotspot' || (item.type === 'original_response' && value?.clicks)) &&
          typeof value === 'object' &&
          value !== null &&
          value.clicks
        ) {
          const clicks = value.clicks as Array<{
            rank: number;
            x: number;
            y: number;
            product_label?: string;
            timestamp_ms: number;
          }>;
          return clicks
            .map(
              (c) =>
                `${c.rank}. ${c.product_label || 'Unknown'} @(${c.x.toFixed(3)}, ${c.y.toFixed(3)}) (${(c.timestamp_ms / 1000).toFixed(1)}s)`,
            )
            .join(', ');
        }

        if (typeof value === 'object' && value?.choice === 'Other') {
          return `Other: ${value.other_text}`;
        }

        if (Array.isArray(value)) {
          return value
            .map((v) => {
              if (typeof v === 'object' && v?.choice === 'Other') {
                return `Other: ${v.other_text}`;
              }
              return v;
            })
            .join(', ');
        }

        return value;
      });

    const result = values.join(', ') || UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.NO_RESPONSE;

    return asJSX ? <div className="text-sm whitespace-pre-wrap">{result}</div> : result;
  };

  const renderConversationFlow = (response: SessionResponse) => {
    const responseData = response.response_data as any[];

    // AI-answered (redundancy skip): show the inferred answer + provenance. Takes priority
    // over the generic renderers since ai_skipped responses carry data + ai_inference.
    if (response.state === 'ai_skipped') {
      const inf: any = response.ai_inference || {};
      const rawAnswer = inf.inferred_answer;
      const answerText = Array.isArray(rawAnswer) ? rawAnswer.join(', ') : String(rawAnswer ?? '');
      // skip-only = a redundancy-audit duplicate that was not asked (no fabricated answer).
      const skipOnly = inf.skip_only === true || rawAnswer === null || rawAnswer === undefined;
      const sourceLabelFor = (qid: string): string => {
        const idx = (sessionData?.responses || []).findIndex((r) => r.question_id === qid);
        return idx >= 0 ? `Q${idx + 1}` : qid;
      };
      const L = UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS;
      return (
        <div className="space-y-3">
          {skipOnly ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
              Skipped — not asked
              {inf.covered_by && inf.covered_by.length > 0 ? (
                <> (covered by {inf.covered_by.map(sourceLabelFor).join(', ')})</>
              ) : null}
              .
            </div>
          ) : (
            <div className="bg-[#E8FCFA] border border-[#07E5D1] rounded-lg p-4">
              <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-[#0a8d82]">
                <Sparkles className="w-3.5 h-3.5" />
                {L.AI_INFERRED_ANSWER}
              </div>
              <div className="text-gray-900 whitespace-pre-wrap">{answerText || L.NO_RESPONSE}</div>
            </div>
          )}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-sm">
            {inf.reason && (
              <div>
                <span className="font-medium text-gray-700">{L.AI_REASON}: </span>
                <span className="text-gray-600">{inf.reason}</span>
              </div>
            )}
            {inf.citation && (
              <div className="border-l-2 border-gray-300 pl-3 italic text-gray-600">
                "{inf.citation}"
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4 pt-1">
              {inf.covered_by && inf.covered_by.length > 0 && (
                <span className="text-gray-600">
                  {L.AI_INFERRED_FROM} {inf.covered_by.map(sourceLabelFor).join(', ')}
                </span>
              )}
              {typeof inf.confidence === 'number' && (
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    inf.confidence >= 0.95
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                      : 'bg-amber-50 border border-amber-200 text-amber-700'
                  }`}
                >
                  {L.AI_CONFIDENCE} {Math.round(inf.confidence * 100)}%
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    // A rapid-fire voice answer awaiting (or that never got) its transcript. Checked by STATE,
    // not by inspecting `value`, ahead of every type-specific renderer below — a bare '' here is
    // otherwise indistinguishable from a respondent who genuinely said nothing.
    if (response.state === 'transcription_pending' || response.state === 'transcription_failed') {
      return renderVoiceTranscriptStatus(response.state);
    }

    if (!responseData || responseData.length === 0) {
      const label =
        response.state === 'conditionally_skipped'
          ? UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.CONDITIONALLY_SKIPPED
          : UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.NO_RESPONSE;
      return (
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-gray-500 whitespace-pre-wrap">{label}</p>
        </div>
      );
    }

    // Check if this is a maxdiff response - render with summary table
    const isMaxDiff = responseData.some(
      (item) =>
        item.type === 'original_response' &&
        typeof item.value === 'object' &&
        item.value !== null &&
        ('most' in item.value || 'least' in item.value),
    );
    if (isMaxDiff) {
      return renderMaxDiffSummary(response);
    }

    // Check if this is a percentage allocation response
    const percentageAllocationItem = responseData.find((item) => {
      if (item.type === 'percentage_allocation') return true;
      if (
        item.type === 'original_response' &&
        typeof item.value === 'object' &&
        item.value !== null &&
        !('most' in item.value) &&
        !('least' in item.value) &&
        !('choice' in item.value) &&
        !('selected_urls' in item.value) &&
        !('clicks' in item.value) &&
        !Array.isArray(item.value)
      ) {
        const entries = Object.entries(item.value);
        // Check if all values are numbers (percentage allocation pattern)
        return entries.length > 0 && entries.every(([, val]) => typeof val === 'number');
      }
      return false;
    });

    if (percentageAllocationItem) {
      const entries = Object.entries(percentageAllocationItem.value);
      const hasFollowup = responseData.some((item) =>
        ['followup_question', 'followup_response'].includes(item.type),
      );
      const followupItems = responseData.filter((item) =>
        ['followup_question', 'followup_response'].includes(item.type),
      );

      return (
        <div className="space-y-4">
          {/* Percentage Allocation Response */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Percentage Allocation:</h5>
            <div className="space-y-2">
              {entries.map(([option, percentage], index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white rounded-lg p-3 border"
                >
                  <span className="text-gray-900 font-medium">{option}</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-teal-500 h-2 rounded-full"
                        style={{ width: `${Math.min(Number(percentage), 100)}%` }}
                      />
                    </div>
                    <div className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-semibold min-w-[50px] text-center">
                      {String(percentage)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Followup Conversation (if any) */}
          {hasFollowup && followupItems.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.CONVERSATION}
              </h4>
              <div className="space-y-3">
                {followupItems.map((item, index) => (
                  <div
                    key={index}
                    className={`flex ${item.type === 'followup_question' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-3/4 ${
                        item.type === 'followup_question'
                          ? 'bg-blue-100 text-blue-900'
                          : 'bg-gray-100 text-gray-900'
                      } rounded-lg p-3`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        {item.type === 'followup_question' && (
                          <span className="text-xs font-medium text-blue-700">
                            🤖 {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.AI_FOLLOWUP}
                          </span>
                        )}
                        {item.type === 'followup_response' && (
                          <span className="text-xs font-medium text-gray-700">
                            👤 {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.USER_RESPONSE}
                          </span>
                        )}
                      </div>
                      <div className="text-sm whitespace-pre-wrap">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Check if this is a conversational response (has followup questions)
    const hasConversation = responseData.some((item) =>
      ['followup_question', 'followup_response'].includes(item.type),
    );

    if (!hasConversation) {
      // Check if this is a ranking response for special display
      const rankingItem = responseData.find((item) => item.type === 'ranking');
      if (rankingItem && Array.isArray(rankingItem.value)) {
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-2">
              {rankingItem.value.map((choice: string, index: number) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <span className="text-gray-900 font-medium">{choice}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Check if this is a matrix rating response for special display
      const matrixRatingItem = responseData.find((item) => item.type === 'matrix_ratings');
      if (
        matrixRatingItem &&
        typeof matrixRatingItem.value === 'object' &&
        matrixRatingItem.value !== null
      ) {
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-3">
              <h5 className="text-sm font-medium text-gray-700 mb-3">Matrix Ratings:</h5>
              {Object.entries(matrixRatingItem.value).map(([item, rating], index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white rounded-lg p-3 border"
                >
                  <span className="text-gray-900 font-medium">{item}</span>
                  <div className="flex items-center space-x-2">
                    <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                      {String(rating)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Check if this is a photo upload response - render per-image view
      const photoUploadItems = responseData.filter(
        (item) =>
          item.type === 'photo_upload' && typeof item.value === 'object' && item.value !== null,
      );
      if (photoUploadItems.length > 0) {
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-4">
              {photoUploadItems.map((photoItem: any, index: number) => (
                <PhotoAnswerImages key={index} value={photoItem.value} />
              ))}
            </div>
          </div>
        );
      }

      // Check if this is a conjoint analysis response for special display
      // Conjoint responses come as 'original_response' with choice/scenario_index structure
      const conjointItems = responseData.filter(
        (item) =>
          item.type === 'original_response' &&
          typeof item.value === 'object' &&
          item.value !== null &&
          'choice' in item.value &&
          'scenario_index' in item.value,
      );
      if (conjointItems.length > 0) {
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-4">
              {conjointItems.map((conjointItem, index) => (
                <div key={index} className="bg-white border-2 border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                        ✓
                      </div>
                      <span className="text-sm font-medium text-blue-800">Selected Choice</span>
                    </div>
                    <span className="text-sm font-medium text-gray-600">
                      Scenario {(conjointItem.value.scenario_index || 0) + 1}
                    </span>
                  </div>
                  <p className="text-blue-900 font-semibold text-lg">
                    {conjointItem.value.choice
                      ?.replace('profile_', 'Option ')
                      ?.replace(/_/g, ' ') || conjointItem.value.choice}
                  </p>

                  {/* Show profile details if available */}
                  {conjointItem.value.profile_details &&
                    typeof conjointItem.value.profile_details === 'object' && (
                      <div className="mt-3 space-y-2">
                        {Object.entries(conjointItem.value.profile_details).map(
                          ([attr, val], idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="font-medium text-gray-700">{attr}:</span>
                              <span className="text-gray-900">{String(val)}</span>
                            </div>
                          ),
                        )}
                      </div>
                    )}

                  <p className="text-gray-600 text-sm mt-2">
                    Profile selected from conjoint analysis choice task
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Check if this is a media response (single_choice or original_response with selected_urls)
      const mediaResponseItems = responseData.filter(
        (item) =>
          (item.type === 'single_choice' || item.type === 'original_response') &&
          typeof item.value === 'object' &&
          item.value?.selected_urls,
      );
      if (mediaResponseItems.length > 0) {
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-4">
              {mediaResponseItems.map((mediaItem: any, index: number) => (
                <div key={index}>
                  <h5 className="text-sm font-medium text-gray-700 mb-3">
                    Selected Media ({mediaItem.value.selected_urls.length} item
                    {mediaItem.value.selected_urls.length !== 1 ? 's' : ''}):
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {mediaItem.value.selected_urls.map((url: string, urlIndex: number) => {
                      const mediaType = getMediaType(url);
                      return (
                        <div
                          key={urlIndex}
                          className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                        >
                          {mediaType === 'image' ? (
                            <img
                              src={url}
                              alt={`Selected media ${urlIndex + 1}`}
                              className="w-full h-32 object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : mediaType === 'video' ? (
                            <video src={url} controls className="w-full h-32" preload="metadata" />
                          ) : (
                            <div className="w-full h-32 flex items-center justify-center bg-gray-100 text-gray-500">
                              <div className="text-center">
                                <FileText className="w-6 h-6 mx-auto mb-1" />
                                <p className="text-xs">Media</p>
                              </div>
                            </div>
                          )}
                          <div className="p-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">
                                Selection #{urlIndex + 1}
                              </span>
                              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-white">
                                <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // Check if this is a multiple choice response for special display
      const multipleChoiceItems = responseData.filter((item) => item.type === 'multiple_choice');
      if (multipleChoiceItems.length > 0) {
        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-3">
              {multipleChoiceItems.map((item: any, index: number) => {
                const chosenValue =
                  typeof item.value === 'object' && item.value?.choice === 'Other'
                    ? 'Other'
                    : item.value;
                const optionMedia = getOptionMediaForChoice(
                  chosenValue,
                  response.choices,
                  response.option_media,
                );

                return (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <span className="text-gray-900 font-medium">
                        {typeof item.value === 'object' && item.value?.choice === 'Other'
                          ? `Other: ${item.value.other_text}`
                          : item.value}
                      </span>
                      {optionMedia.length > 0 && renderOptionMediaThumbnails(optionMedia)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // Check if this is an image_hotspot response for special display
      // Also check for original_response with clicks (image_hotspot data during follow-ups)
      const imageHotspotItem = responseData.find(
        (item) =>
          (item.type === 'image_hotspot' || item.type === 'original_response') &&
          typeof item.value === 'object' &&
          item.value !== null &&
          'clicks' in item.value,
      );
      if (
        imageHotspotItem &&
        typeof imageHotspotItem.value === 'object' &&
        imageHotspotItem.value?.clicks
      ) {
        const clicks = imageHotspotItem.value.clicks as Array<{
          rank: number;
          x: number;
          y: number;
          product_id?: string;
          product_label?: string;
          timestamp_ms: number;
        }>;

        // Get rank colors for visual distinction
        const getRankColor = (rank: number): string => {
          const colors = [
            'bg-teal-500', // Rank 1
            'bg-blue-500', // Rank 2
            'bg-purple-500', // Rank 3
            'bg-orange-500', // Rank 4
            'bg-pink-500', // Rank 5
          ];
          return colors[(rank - 1) % colors.length];
        };

        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Image Hotspot Selections:</h5>
            <div className="space-y-2">
              {clicks.map((click, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-white rounded-lg p-3 border border-gray-200"
                >
                  <div
                    className={`w-7 h-7 ${getRankColor(click.rank)} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}
                  >
                    {click.rank}
                  </div>
                  <div className="flex-1">
                    <span className="text-gray-900 font-medium">
                      {click.product_label || 'Unknown Region'}
                    </span>
                    <div className="text-xs text-gray-500">
                      Position: ({click.x.toFixed(3)}, {click.y.toFixed(3)})
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-600">
                      {(click.timestamp_ms / 1000).toFixed(1)}s
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {/* Total response time */}
            {clicks.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                Total response time:{' '}
                <span className="font-medium">
                  {(clicks[clicks.length - 1].timestamp_ms / 1000).toFixed(1)}s
                </span>
              </div>
            )}
          </div>
        );
      }

      // Check if this is a single_choice response with option media
      const singleChoiceItem = responseData.find(
        (item) => item.type === 'single_choice' || item.type === 'original_response',
      );
      if (singleChoiceItem && response.option_media && response.choices) {
        const chosenValue =
          typeof singleChoiceItem.value === 'string' ? singleChoiceItem.value : null;
        const optionMedia = chosenValue
          ? getOptionMediaForChoice(chosenValue, response.choices, response.option_media)
          : [];

        return (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <span className="text-gray-900 font-medium">{chosenValue}</span>
                {optionMedia.length > 0 && renderOptionMediaThumbnails(optionMedia)}
              </div>
            </div>
          </div>
        );
      }

      // Simple response - just show the values
      return (
        <div className="bg-gray-50 rounded-lg p-4">
          {renderResponse(responseData, true, response.state) as React.ReactElement}
        </div>
      );
    }

    // Conversational response - show chat-like interface
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.CONVERSATION}
        </h4>
        <div className="space-y-3">
          {responseData.map((item, index) => (
            <div
              key={index}
              className={`flex ${item.type === 'followup_question' ? 'justify-start' : 'justify-end'}`}
            >
              <div
                className={`max-w-3/4 ${
                  item.type === 'followup_question'
                    ? 'bg-blue-100 text-blue-900'
                    : 'bg-gray-100 text-gray-900'
                } rounded-lg p-3`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  {item.type === 'followup_question' && (
                    <span className="text-xs font-medium text-blue-700">
                      🤖 {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.AI_FOLLOWUP}
                    </span>
                  )}
                  {(item.type === 'original_response' || item.type === 'followup_response') && (
                    <span className="text-xs font-medium text-gray-700">
                      👤 {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.USER_RESPONSE}
                    </span>
                  )}
                </div>
                {/* Render all responses using unified function */}
                {renderResponse([item], true, response.state) as React.ReactElement}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Aggregate maxdiff claims from response data
   * Returns ranked claims with counts and scores
   */
  const aggregateMaxDiffClaims = (responseData: ResponseItem[]) => {
    // Filter maxdiff items
    const maxDiffItems = responseData.filter(
      (item) =>
        item.type === 'original_response' &&
        typeof item.value === 'object' &&
        item.value !== null &&
        ('most' in item.value || 'least' in item.value),
    );

    if (maxDiffItems.length === 0) return null;

    // Count occurrences
    const claimCounts: Record<string, { most: number; least: number }> = {};

    maxDiffItems.forEach((item) => {
      const value = item.value as any;

      if (value.most) {
        if (!claimCounts[value.most]) {
          claimCounts[value.most] = { most: 0, least: 0 };
        }
        claimCounts[value.most].most++;
      }

      if (value.least) {
        if (!claimCounts[value.least]) {
          claimCounts[value.least] = { most: 0, least: 0 };
        }
        claimCounts[value.least].least++;
      }
    });

    const totalSets = maxDiffItems.length;

    // Calculate raw count net scores (most_count - least_count)
    const claims = Object.keys(claimCounts).map((claim) => {
      const counts = claimCounts[claim];
      const netScore = counts.most - counts.least;
      const mostPercentage = (counts.most / totalSets) * 100;
      const leastPercentage = (counts.least / totalSets) * 100;

      return {
        claim,
        most_count: counts.most,
        least_count: counts.least,
        net_score: netScore,
        most_percentage: mostPercentage,
        least_percentage: leastPercentage,
        rank: 0, // Will be assigned after sorting
      };
    });

    // Sort by net score (descending)
    claims.sort((a, b) => b.net_score - a.net_score);

    // Assign ranks (handle ties)
    claims.forEach((claim, index) => {
      if (index > 0 && claim.net_score === claims[index - 1].net_score) {
        claim.rank = claims[index - 1].rank;
      } else {
        claim.rank = index + 1;
      }
    });

    return {
      claims,
      totalSets,
    };
  };

  /**
   * Render maxdiff summary table
   */
  const renderMaxDiffSummary = (response: SessionResponse) => {
    const aggregated = aggregateMaxDiffClaims(response.response_data);

    if (!aggregated) return null;

    const { claims, totalSets } = aggregated;
    const totalClaims = claims.length;

    const getClaimRowColor = (rank: number): string => {
      if (rank <= 3) return 'bg-green-50';
      if (rank > totalClaims - 3) return 'bg-red-50';
      return 'bg-white';
    };

    return (
      <div className="">
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Aggregated rankings based on {totalSets} set{totalSets !== 1 ? 's' : ''} completed. Net
            Score = (Most Selected) - (Least Selected)
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Claim
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 mr-1 text-green-600" />
                    Most Selected
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center justify-center">
                    <TrendingDown className="w-4 h-4 mr-1 text-red-600" />
                    Least Selected
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Net Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {claims.map((claim, index) => (
                <tr
                  key={index}
                  className={`${getClaimRowColor(claim.rank)} hover:opacity-75 transition-opacity`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span
                        className={`text-sm font-semibold ${
                          claim.rank <= 3
                            ? 'text-green-700'
                            : claim.rank > totalClaims - 3
                              ? 'text-red-700'
                              : 'text-gray-700'
                        }`}
                      >
                        #{claim.rank}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-md">{claim.claim}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="text-sm font-medium text-gray-900">{claim.most_count}</div>
                    <div className="text-xs text-gray-500">
                      ({claim.most_percentage.toFixed(1)}%)
                    </div>
                    {/* Progress bar */}
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-green-600 h-1.5 rounded-full"
                        style={{ width: `${Math.min(claim.most_percentage, 100)}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="text-sm font-medium text-gray-900">{claim.least_count}</div>
                    <div className="text-xs text-gray-500">
                      ({claim.least_percentage.toFixed(1)}%)
                    </div>
                    {/* Progress bar */}
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-red-600 h-1.5 rounded-full"
                        style={{ width: `${Math.min(claim.least_percentage, 100)}%` }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                        claim.net_score > 0
                          ? 'bg-green-100 text-green-800'
                          : claim.net_score < 0
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {claim.net_score > 0 ? '+' : ''}
                      {claim.net_score}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const goBackToResponses = () => {
    // Navigate back to the survey responses list
    // We need to extract the survey ID from somewhere or pass it through navigation
    navigate(-1); // Simple back navigation for now
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LOADING.LOADING_SESSION}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !sessionData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-center py-8">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.TITLES.UNABLE_TO_LOAD_SESSION}
              </h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button
                variant="primary"
                onClick={goBackToResponses}
                icon={<ArrowLeft className="w-4 h-4" />}
              >
                {UI_TEXT.BUTTONS.GO_BACK}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sessionData) return null;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              onClick={goBackToResponses}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.BUTTONS.BACK_TO_RESPONSES}
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-2">
                <MessageSquare className="w-6 h-6 text-blue-500 mr-2" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.TITLES.RESPONSE_DETAILS}
                </h1>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="w-4 h-4 mr-1" />
                {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.DESCRIPTIONS.COMPLETED_ON}{' '}
                {formatDate(sessionData.completed_at)}
              </div>
            </div>

            {/* Responses List */}
            {sessionData.responses.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {sessionData.responses.map((response, index) => (
                  <div key={index} className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <span className="text-sm font-medium text-gray-500 mr-3">
                          {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.QUESTION_NUMBER(index + 1)}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {response.question_type.replace('_', ' ')}
                        </span>
                        {response.state === 'ai_skipped' && (
                          <span
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#E8FCFA] text-[#0a8d82] border border-[#07E5D1] ml-2"
                            title={
                              UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.AI_ANSWERED_TOOLTIP
                            }
                          >
                            <Sparkles className="w-3 h-3 mr-1" />
                            {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.LABELS.AI_ANSWERED}
                          </span>
                        )}
                        {renderInputModeIndicator(response.input_mode)}
                        {response.response_time_seconds !== undefined &&
                          response.response_time_seconds !== null && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 ml-2">
                              {response.response_time_seconds.toFixed(1)}s
                            </span>
                          )}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">
                        {resolveQuestionHeading(response)}
                      </h3>
                      {response.adaptation && (
                        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                          {response.adaptation.action === 'skip' ? (
                            <span className="text-amber-800">
                              <span className="font-semibold">
                                Skipped — its rubric was fully covered earlier
                              </span>
                              {response.adaptation.covered_by &&
                              response.adaptation.covered_by.length > 0
                                ? ` (carried forward from ${response.adaptation.covered_by.join(', ')})`
                                : ''}
                            </span>
                          ) : (
                            <div className="text-amber-800">
                              <span className="font-semibold">Adapted (narrowed):</span> asked “
                              {response.adaptation.adapted_question}”
                              {response.adaptation.covered_points &&
                                response.adaptation.covered_points.length > 0 && (
                                  <div className="mt-1 text-amber-700">
                                    Already covered earlier:{' '}
                                    {response.adaptation.covered_points.join('; ')}
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      )}
                      {renderQualityFlags(response.quality_flags)}
                    </div>

                    {/* Media Content */}
                    {renderMediaContent(response.media_urls)}

                    {renderConversationFlow(response)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.TITLES.NO_RESPONSES_FOUND}
                </h3>
                <p className="text-gray-600">
                  {UI_TEXT.HUMAN_SURVEY.SESSION_RESPONSES.DESCRIPTIONS.NO_SESSION_RESPONSES}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SessionResponses;
